import type { Apis } from '@efdigital/ll-addon-auth/types/apis';
import config from '@efdigital/ll-admin-v2/config/environment';
import { DataTypeLti } from '@efdigital/ll-admin-v2/data/data-types/lti';
import { LTI_API_PATH } from '@efdigital/ll-admin-v2/data/handlers/lti/lti-consuming-platform-handler.ts';
import type { LtiConsumedToolDeployment } from '@efdigital/ll-admin-v2/data/types/lti';
import { withReactiveResponse } from '@warp-drive/core/request';
import {
  createRecord,
  deleteRecord,
  updateRecord,
} from '@warp-drive/utilities/json-api';

import { LtiOperation } from './ops.ts';

const apis: Apis = config['ll-addon-auth-apis'];

function deploymentBaseUrl(platformId: string, consumedToolId: string): string {
  return `${apis.systemsApi.host}${LTI_API_PATH}/platforms/${encodeURIComponent(platformId)}/tools/${encodeURIComponent(consumedToolId)}/deployments`;
}

function consumedToolDeploymentRequestBody(
  deployment: LtiConsumedToolDeployment
) {
  return {
    platformDeploymentId: deployment.platformDeploymentId,
    resourceLinkClaimPath: deployment.resourceLinkClaimPath,
    isActive: deployment.isActive,
  };
}

export function buildListConsumedToolDeployments(
  platformId: string,
  consumedToolId: string
) {
  const url = deploymentBaseUrl(platformId, consumedToolId);

  return withReactiveResponse<LtiConsumedToolDeployment[]>({
    url,
    method: 'GET',
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    op: LtiOperation.ListConsumedToolDeployments,
    cacheOptions: {
      key: url,
      types: [DataTypeLti.ConsumedToolDeployment],
    },
  });
}

export function buildCreateConsumedToolDeployment(
  deployment: LtiConsumedToolDeployment,
  platformId: string,
  consumedToolId: string
) {
  const init = createRecord(deployment, {});

  init.url = deploymentBaseUrl(platformId, consumedToolId);
  init.body = JSON.stringify(consumedToolDeploymentRequestBody(deployment));
  init.headers.set('Content-Type', 'application/json');

  return init;
}

export function buildUpdateConsumedToolDeployment(
  deployment: LtiConsumedToolDeployment,
  platformId: string,
  consumedToolId: string
) {
  const deploymentId = deployment.id;
  if (!deploymentId) {
    throw new Error('Cannot update a consumed tool deployment without an id');
  }

  const init = updateRecord(deployment, {});

  init.url = `${deploymentBaseUrl(platformId, consumedToolId)}/${encodeURIComponent(deploymentId)}`;
  init.body = JSON.stringify(consumedToolDeploymentRequestBody(deployment));
  init.headers.set('Content-Type', 'application/json');

  return init;
}

export function buildDeleteConsumedToolDeployment(
  deployment: LtiConsumedToolDeployment,
  platformId: string,
  consumedToolId: string
) {
  const deploymentId = deployment.id;
  if (!deploymentId) {
    throw new Error('Cannot delete a consumed tool deployment without an id');
  }

  const init = deleteRecord(deployment, {});

  init.url = `${deploymentBaseUrl(platformId, consumedToolId)}/${encodeURIComponent(deploymentId)}`;
  init.headers.set('Content-Type', 'application/json');

  return init;
}
